import React from 'react';
import { Info, Calendar, CalendarDays, Briefcase, Calculator, User, CalendarCheck, CalendarX } from 'lucide-react';

/**
 * Компонент с информацией о расчетах дат и времени
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-cyan-500" />
        Всё о расчетах промежутка времени
      </h2>
      
      <div className="prose prose-cyan dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-cyan-500" />
          Для чего нужен расчет промежутка времени?
        </h3>
        <p>
          <strong>Расчет промежутка времени</strong> — это определение точного количества дней, недель, месяцев и лет между двумя датами. Он применяется в различных сферах: от планирования проектов и расчета сроков договоров до определения возраста и стажа работы. В России особенно важно учитывать производственный календарь с официальными праздниками и переносами выходных дней.
        </p>

        <h3 className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-cyan-500" />
          Методы расчета промежутка времени
        </h3>
        <ul>
          <li><strong>Календарные дни</strong> — все дни подряд между начальной и конечной датой, включая выходные и праздники.</li>
          <li><strong>Рабочие дни</strong> — только будние дни (понедельник-пятница) без учета официальных праздников и переносов.</li>
          <li><strong>Включение/исключение конечной даты</strong> — в зависимости от типа расчета, конечная дата может либо включаться в итоговый результат, либо исключаться.</li>
          <li>
            <strong>Разные единицы измерения:</strong>
            <ul>
              <li><strong>Дни</strong> — самая точная единица для небольших промежутков.</li>
              <li><strong>Недели</strong> — удобно для периодов в несколько недель (7 дней = 1 неделя).</li>
              <li><strong>Месяцы</strong> — используются для более длительных периодов, но могут быть неточными из-за разной продолжительности месяцев.</li>
              <li><strong>Годы</strong> — применяются для долгосрочных периодов (365 или 366 дней в високосном году).</li>
            </ul>
          </li>
        </ul>

        <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-100 dark:border-cyan-800 my-6">
          <h4 className="text-cyan-700 dark:text-cyan-300 font-medium">Особенности российского производственного календаря</h4>
          <p className="text-cyan-600 dark:text-cyan-400 text-sm mb-0">
            В России официальный производственный календарь учитывает федеральные праздники и переносы выходных дней. Нерабочими праздничными днями являются: 1-8 января (Новогодние каникулы и Рождество), 23 февраля, 8 марта, 1 мая, 9 мая, 12 июня и 4 ноября. При совпадении выходного и праздничного дней выходной переносится на следующий после праздничного рабочий день.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-cyan-500" />
          Практическое применение расчета рабочих дней
        </h3>
        <p>
          Расчет рабочих дней особенно важен в следующих областях:
        </p>
        <ul>
          <li><strong>Трудовые отношения</strong> — расчет отпусков, больничных, выходных пособий и стажа работы.</li>
          <li><strong>Деловые контракты</strong> — определение сроков исполнения обязательств с учетом только рабочих дней.</li>
          <li><strong>Проектный менеджмент</strong> — планирование сроков выполнения задач и проектов.</li>
          <li><strong>Финансы</strong> — расчет банковских дней для начисления процентов, сроков кредитных договоров.</li>
          <li><strong>Логистика</strong> — расчет времени доставки с учетом рабочих дней.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-cyan-500" />
          Добавление и вычитание периодов времени
        </h3>
        <p>
          Добавление или вычитание периодов времени от заданной даты позволяет определить будущие или прошедшие даты. Это полезно для:
        </p>
        <ul>
          <li><strong>Планирования событий</strong> — определение дат будущих событий (например, через 90 дней после подписания договора).</li>
          <li><strong>Расчета крайних сроков</strong> — например, последний день оплаты счета, срок действия документов.</li>
          <li><strong>Определения ключевых дат в прошлом</strong> — например, дата зачатия по предполагаемой дате родов.</li>
          <li><strong>Планирования регулярных событий</strong> — например, даты платежей по кредиту.</li>
        </ul>
        
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2">
            <CalendarCheck className="w-4 h-4" />
            Полезные формулы расчета дат
          </h4>
          <ul className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            <li><strong>Для кредитных договоров:</strong> Срок погашения = Дата выдачи + Срок кредита (в месяцах)</li>
            <li><strong>Для беременности:</strong> Предполагаемая дата родов = Дата последней менструации + 280 дней</li>
            <li><strong>Для документов:</strong> Срок действия = Дата выдачи + Срок (обычно 30, 90, 365 дней)</li>
            <li><strong>Для отпуска:</strong> Дата выхода на работу = Первый день отпуска + Количество дней отпуска</li>
            <li><strong>Для трудового стажа:</strong> Стаж = Текущая дата − Дата начала работы</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <User className="w-5 h-5 text-cyan-500" />
          Расчет возраста и жизненных этапов
        </h3>
        <p>
          Расчет возраста используется для определения точного количества лет, месяцев и дней с момента рождения человека. Это важно для:
        </p>
        <ul>
          <li><strong>Юридических документов</strong> — паспорт, водительское удостоверение, пенсионное удостоверение.</li>
          <li><strong>Медицинских карт</strong> — точный возраст для диагностики и лечения.</li>
          <li><strong>Образовательных целей</strong> — определение готовности к школе, университету.</li>
          <li><strong>Страхования</strong> — расчет страховых премий в зависимости от возраста.</li>
        </ul>
        
        <h3 className="flex items-center gap-2">
          <CalendarX className="w-5 h-5 text-cyan-500" />
          Особенности расчета в различных системах
        </h3>
        <p>
          В разных культурах и сферах возраст и время могут рассчитываться по-разному:
        </p>
        <ul>
          <li><strong>Юридический возраст</strong> — в большинстве стран, включая Россию, человек считается достигшим нового возраста в день рождения.</li>
          <li><strong>Восточноазиатский возраст</strong> — в некоторых восточноазиатских культурах ребенок считается годовалым при рождении, а новый год добавляет всем еще один год.</li>
          <li><strong>Академический возраст</strong> — в образовательных учреждениях часто используется возраст на 1 сентября для определения класса/группы.</li>
          <li><strong>Лунный календарь</strong> — некоторые культуры используют лунный календарь для расчета дат, что может влиять на определение возраста и праздников.</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор промежутка времени учитывает особенности российского календаря и предлагает различные методы расчета для всех ваших потребностей!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;